import { useState, useMemo, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useDiarioClasse } from '../../api/hooks/useDiarioClasse';
import { useProfessorTurmas } from '../../api/hooks/useProfessorTurmas';
import {
  BookOpen, Calendar, FileText, Lightbulb, Wrench, CheckCircle2,
  XCircle, Search, ChevronDown, ChevronUp, Edit3, Save, X, NotebookPen,
  Filter, Users, ClipboardList,
} from 'lucide-react';
import type { DiarioClasse } from '../../api/hooks/useDiarioClasse';
import type { Materia } from '../../types/models';
import { useCrud } from '../../api/hooks/useCrud';
import type { UpdateDiarioClasseInput } from '../../api/hooks/useDiarioClasse';
import { MobilePageWrapper } from '../../components/layout/MobilePageWrapper';
import { DiarioProfessorDesktop } from './DiarioProfessorDesktop';
import { useHojeReferencia } from '../../api/hooks/useSimClock';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < breakpoint : false),
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

function ResumoChamadaDiario({ chamada }: { chamada?: DiarioClasse['chamada'] }) {
  if (!chamada) return null;
  const total = chamada.totalAlunos ?? 0;
  const presentes = chamada.totalPresentes ?? 0;
  const faltas = chamada.totalAusentes ?? 0;
  const atrasos = chamada.totalAtrasados ?? 0;
  if (total === 0 && presentes === 0 && faltas === 0) return null;

  return (
    <div className="diario-chamada-row" onClick={(e) => e.stopPropagation()}>
      <span className="diario-chamada-pill total" title="Alunos na turma na chamada">
        <Users size={10} style={{ marginRight: 3, verticalAlign: -1 }} />
        {total} alunos
      </span>
      <span className="diario-chamada-pill presente">{presentes} presentes</span>
      {faltas > 0 && <span className="diario-chamada-pill falta">{faltas} faltas</span>}
      {atrasos > 0 && <span className="diario-chamada-pill atraso">{atrasos} atrasos</span>}
    </div>
  );
}

export function DiarioClassePage() {
  const user = useAuthStore((s) => s.user);
  const isMobile = useIsMobile();
  const { hojeStr, isSimulation } = useHojeReferencia();
  const hoje = hojeStr || new Date().toISOString().split('T')[0];

  const { data: materias } = useCrud<Materia>('materias');
  const { turmas: turmasGrade } = useProfessorTurmas(user?.id);

  const [dataSelecionada, setDataSelecionada] = useState(hoje);
  useEffect(() => {
    if (isSimulation && hojeStr && /^\d{4}-\d{2}-\d{2}$/.test(hojeStr)) {
      setDataSelecionada(hojeStr);
    }
  }, [isSimulation, hojeStr]);
  const [filtroTurma, setFiltroTurma] = useState('');
  const [filtroMateria, setFiltroMateria] = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState<'semana' | 'mes' | 'bimestre' | 'todos'>(
    isMobile ? 'mes' : 'bimestre',
  );
  const [busca, setBusca] = useState('');
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const periodoFiltros = useMemo(() => {
    const ref =
      hoje && /^\d{4}-\d{2}-\d{2}$/.test(hoje)
        ? new Date(`${hoje}T12:00:00`)
        : new Date();
    let dataInicio: string | undefined;
    const dataFim = `${ref.getFullYear()}-${String(ref.getMonth() + 1).padStart(2, '0')}-${String(ref.getDate()).padStart(2, '0')}`;

    if (filtroPeriodo === 'semana') {
      const d = new Date(ref);
      d.setDate(d.getDate() - 7);
      dataInicio = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } else if (filtroPeriodo === 'mes') {
      const d = new Date(ref);
      d.setMonth(d.getMonth() - 1);
      dataInicio = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } else if (filtroPeriodo === 'bimestre') {
      const d = new Date(ref);
      d.setMonth(d.getMonth() - 2);
      dataInicio = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    return {
      professorId: user?.id,
      turmaId: filtroTurma || undefined,
      materiaId: filtroMateria || undefined,
      dataInicio,
      dataFim: filtroPeriodo !== 'todos' ? dataFim : undefined,
    };
  }, [user?.id, filtroTurma, filtroMateria, filtroPeriodo, hoje]);

  const { diarios, isLoading, atualizar } = useDiarioClasse(periodoFiltros);

  const turmasOpcoes = useMemo(() => {
    const map = new Map<string, string>();
    diarios.forEach((d) => {
      if (d.turma?.id && d.turma?.nome) map.set(d.turma.id, d.turma.nome);
    });
    turmasGrade.forEach((t) => map.set(t.id, t.nome));
    return Array.from(map.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [diarios, turmasGrade]);

  const turmasProfessor = turmasOpcoes;

  const materiasOpcoes = useMemo(() => {
    const map = new Map<string, string>();
    diarios.forEach((d) => {
      if (d.materia?.id && d.materia?.nome) map.set(d.materia.id, d.materia.nome);
    });
    return Array.from(map.entries())
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [diarios]);

  const countByDay = useMemo(() => {
    const m: Record<string, number> = {};
    diarios.forEach((d) => {
      const iso = d.dataReferencia?.split('T')[0];
      if (iso) m[iso] = (m[iso] || 0) + 1;
    });
    return m;
  }, [diarios]);

  const diariosFiltrados = useMemo(() => {
    if (!busca.trim()) return diarios;
    const termo = busca.toLowerCase();
    return diarios.filter(
      (d) =>
        d.conteudoMinistrado?.toLowerCase().includes(termo) ||
        d.metodologia?.toLowerCase().includes(termo) ||
        d.observacoes?.toLowerCase().includes(termo) ||
        d.turma?.nome?.toLowerCase().includes(termo) ||
        d.materia?.nome?.toLowerCase().includes(termo),
    );
  }, [diarios, busca]);

  const totalRegistros = diariosFiltrados.length;
  const registrosHoje = diariosFiltrados.filter((d) => d.dataReferencia?.startsWith(hoje)).length;
  const objetivosCumpridos = diariosFiltrados.filter((d) => d.objetivosCumpridos !== false).length;

  const gruposPorData = useMemo(() => {
    const map = new Map<string, typeof diariosFiltrados>();
    diariosFiltrados.forEach((d) => {
      const data = d.dataReferencia?.split('T')[0] || 'sem-data';
      if (!map.has(data)) map.set(data, []);
      map.get(data)!.push(d);
    });
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [diariosFiltrados]);

  const materiasProfessor = useMemo(() => {
    const ids = new Set<string>();
    diarios.forEach((d) => {
      if (d.materiaId) ids.add(d.materiaId);
    });
    return materias.filter((m) => ids.has(m.id)).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [materias, diarios]);

  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editDados, setEditDados] = useState<UpdateDiarioClasseInput & { id: string }>({ id: '' });
  const [salvandoEdit, setSalvandoEdit] = useState(false);
  const [desktopEditKey, setDesktopEditKey] = useState(0);

  const iniciarEdicao = (d: DiarioClasse) => {
    if (d.editavel === false) return;
    setEditandoId(d.id);
    setEditDados({
      id: d.id,
      conteudoMinistrado: d.conteudoMinistrado || '',
      metodologia: d.metodologia || '',
      recursosUtilizados: d.recursosUtilizados || '',
      observacoes: d.observacoes || '',
      objetivosCumpridos: d.objetivosCumpridos ?? true,
    });
  };

  const salvarEdicao = (input?: UpdateDiarioClasseInput & { id: string }) => {
    const payload = input ?? editDados;
    if (!payload.id) return;
    setSalvandoEdit(true);
    atualizar.mutate(payload as UpdateDiarioClasseInput & { id: string }, {
      onSuccess: () => {
        setEditandoId(null);
        setSalvandoEdit(false);
        setDesktopEditKey((k) => k + 1);
      },
      onError: () => setSalvandoEdit(false),
    });
  };

  const cancelarEdicao = () => setEditandoId(null);

  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const toggleExpandido = (id: string) => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtrosAtivos = [filtroTurma, filtroMateria, filtroPeriodo !== 'mes' ? filtroPeriodo : ''].filter(
    Boolean,
  ).length;

  if (!isMobile) {
    return (
      <div className="diar-desk-host" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DiarioProfessorDesktop
          key={desktopEditKey}
          professorId={user?.id ?? ''}
          diarios={diarios}
          isLoading={isLoading}
          dataSelecionada={dataSelecionada}
          setDataSelecionada={setDataSelecionada}
          busca={busca}
          setBusca={setBusca}
          filtroTurma={filtroTurma}
          setFiltroTurma={setFiltroTurma}
          filtroMateria={filtroMateria}
          setFiltroMateria={setFiltroMateria}
          filtroPeriodo={filtroPeriodo}
          setFiltroPeriodo={setFiltroPeriodo}
          turmasOpcoes={turmasOpcoes}
          materiasOpcoes={materiasOpcoes}
          countByDay={countByDay}
          onSalvar={salvarEdicao}
          salvandoEdit={salvandoEdit}
        />
      </div>
    );
  }

  return (
    <MobilePageWrapper
      title="Diário de Classe"
      icon={NotebookPen}
      iconColor="#93c5fd"
      badge={registrosHoje > 0 ? `${registrosHoje} hoje` : undefined}
    >
      <div className="admin-page">
        <div className="page-header" style={{ marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', display: 'flex', alignItems: 'center', gap: 10 }}>
              <BookOpen size={24} style={{ color: '#8b5cf6' }} />
              Diário de Classe
            </h1>
            <p style={{ opacity: 0.7 }}>Registro de conteúdos, metodologias e observações das aulas</p>
          </div>
        </div>

        <div className="kpi-grid kpi-grid-4" style={{ marginBottom: 10 }}>
          <div className="kpi-card">
            <div className="kpi-card-info">
              <h3>Total</h3>
              <span className="kpi-value">{totalRegistros}</span>
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-card-info">
              <h3>Hoje</h3>
              <span className="kpi-value" style={{ color: registrosHoje > 0 ? '#16a34a' : '#d97706' }}>
                {registrosHoje}
              </span>
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-card-info">
              <h3>Objetivos</h3>
              <span className="kpi-value" style={{ color: '#16a34a' }}>{objetivosCumpridos}</span>
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-card-info">
              <h3>Turmas</h3>
              <span className="kpi-value">{turmasOpcoes.length}</span>
            </div>
          </div>
        </div>

        <div className="diario-filter-bar">
          <Search
            size={14}
            style={{
              position: 'absolute',
              left: 9,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          <input
            type="text"
            placeholder="Buscar conteudo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="diario-search-input"
          />
          <button
            className={`diario-filter-btn ${filtrosAbertos ? 'active' : ''}`}
            onClick={() => setFiltrosAbertos(!filtrosAbertos)}
            aria-label="Filtros"
          >
            <Filter size={15} />
            {filtrosAtivos > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: '#ef4444',
                  color: '#fff',
                  fontSize: '0.5rem',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {filtrosAtivos}
              </span>
            )}
          </button>
        </div>

        {filtrosAbertos && (
          <div className="diario-filter-panel">
            <select value={filtroTurma} onChange={(e) => setFiltroTurma(e.target.value)}>
              <option value="">Turmas</option>
              {turmasProfessor.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
            <select value={filtroMateria} onChange={(e) => setFiltroMateria(e.target.value)}>
              <option value="">Materias</option>
              {materiasProfessor.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            </select>
            <select value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.target.value as typeof filtroPeriodo)}>
              <option value="semana">Semana</option>
              <option value="mes">Mes</option>
              <option value="bimestre">Bimestre</option>
              <option value="todos">Todos</option>
            </select>
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted, #999)' }}>Carregando...</div>
        ) : diariosFiltrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
            <BookOpen size={32} style={{ margin: '0 auto 10px', color: '#d1d5db' }} />
            <p style={{ fontWeight: 600, fontSize: '0.82rem' }}>Nenhum registro encontrado</p>
            <p style={{ fontSize: '0.72rem', opacity: 0.7 }}>
              Os registros aparecem aqui apos voce concluir uma chamada
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {gruposPorData.map(([data, registros]) => (
              <div key={data} className="diario-date-group">
                <div className="diario-date-header">
                  <Calendar size={12} style={{ color: '#6b7280' }} />
                  <span className="diario-date-label">
                    {new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', {
                      weekday: 'short',
                      day: '2-digit',
                      month: 'short',
                    })}
                  </span>
                  <span className={`diario-date-badge ${data === hoje ? 'today' : ''}`}>
                    {data === hoje ? 'Hoje' : `${registros.length}`}
                  </span>
                </div>

                <div className="diario-entries">
                  {registros.map((d) => {
                    const isExpanded = expandidos.has(d.id);
                    const isEditing = editandoId === d.id;

                    return (
                      <div key={d.id} className={`diario-entry ${isEditing ? 'editing' : ''}`}>
                        <div
                          className="diario-entry-header"
                          onClick={() => !isEditing && toggleExpandido(d.id)}
                        >
                          <div className={`diario-entry-icon ${d.objetivosCumpridos !== false ? 'ok' : 'fail'}`}>
                            {d.objetivosCumpridos !== false ? (
                              <CheckCircle2 size={14} style={{ color: '#16a34a' }} />
                            ) : (
                              <XCircle size={14} style={{ color: '#dc2626' }} />
                            )}
                          </div>

                          <div className="diario-entry-content">
                            <div className={`diario-entry-title ${isExpanded ? 'expanded' : ''}`}>
                              {d.conteudoMinistrado || 'Sem conteudo registrado'}
                            </div>
                            <div className="diario-entry-tags">
                              {d.turma?.nome && (
                                <span className="diario-entry-tag turma">{d.turma.nome}</span>
                              )}
                              {d.materia?.nome && (
                                <span className="diario-entry-tag materia">{d.materia.nome}</span>
                              )}
                            </div>
                            <ResumoChamadaDiario chamada={d.chamada} />
                          </div>

                          <div className="diario-entry-actions">
                            {!isEditing && d.editavel !== false && (
                              <button
                                className="diario-entry-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  iniciarEdicao(d);
                                }}
                                title="Editar"
                              >
                                <Edit3 size={13} />
                              </button>
                            )}
                            {!isEditing && d.editavel === false && (
                              <span
                                title="Edicao bloqueada apos 7 dias. Solicite a coordenacao."
                                style={{
                                  fontSize: '0.58rem',
                                  color: '#9ca3af',
                                  padding: '0 4px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Somente leitura
                              </span>
                            )}
                            {!isEditing &&
                              (isExpanded ? (
                                <ChevronUp size={14} style={{ color: '#9ca3af' }} />
                              ) : (
                                <ChevronDown size={14} style={{ color: '#9ca3af' }} />
                              ))}
                          </div>
                        </div>

                        {isExpanded && !isEditing && (
                          <div className="diario-entry-body">
                            {d.chamada && (d.chamada.totalAlunos ?? 0) > 0 && (
                              <div className="diario-field-row">
                                <ClipboardList size={12} style={{ color: '#2563eb', flexShrink: 0, marginTop: 1 }} />
                                <div style={{ flex: 1 }}>
                                  <div className="diario-field-label">Chamada</div>
                                  <div className="diario-field-value">
                                    {d.chamada.totalAlunos} alunos · {d.chamada.totalPresentes ?? 0} presentes ·{' '}
                                    {d.chamada.totalAusentes ?? 0} faltas
                                    {(d.chamada.totalAtrasados ?? 0) > 0
                                      ? ` · ${d.chamada.totalAtrasados} atrasos`
                                      : ''}
                                    {d.chamada.status === 'CONCLUIDA' ? ' · Concluída' : ''}
                                  </div>
                                </div>
                              </div>
                            )}
                            {d.metodologia && (
                              <div className="diario-field-row">
                                <Lightbulb size={12} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                                <div>
                                  <div className="diario-field-label">Metodologia</div>
                                  <div className="diario-field-value">{d.metodologia}</div>
                                </div>
                              </div>
                            )}
                            {d.recursosUtilizados && (
                              <div className="diario-field-row">
                                <Wrench size={12} style={{ color: '#6366f1', flexShrink: 0, marginTop: 1 }} />
                                <div>
                                  <div className="diario-field-label">Recursos</div>
                                  <div className="diario-field-value">{d.recursosUtilizados}</div>
                                </div>
                              </div>
                            )}
                            {d.observacoes && (
                              <div className="diario-field-row">
                                <FileText size={12} style={{ color: '#059669', flexShrink: 0, marginTop: 1 }} />
                                <div>
                                  <div className="diario-field-label">Observacoes</div>
                                  <div className="diario-field-value">{d.observacoes}</div>
                                </div>
                              </div>
                            )}
                            <div className="diario-meta">
                              {d.professor?.nome ? `${d.professor.nome}` : ''}{' '}
                              {d.criadoEm ? `· ${new Date(d.criadoEm).toLocaleString('pt-BR')}` : ''}
                            </div>
                          </div>
                        )}

                        {isEditing && (
                          <div className="diario-edit-body">
                            <div className="diario-edit-field">
                              <label>
                                <FileText size={10} /> Conteudo *
                              </label>
                              <textarea
                                value={editDados.conteudoMinistrado || ''}
                                onChange={(e) =>
                                  setEditDados((p) => ({ ...p, conteudoMinistrado: e.target.value }))
                                }
                                rows={2}
                              />
                            </div>
                            <div className="diario-edit-field">
                              <label>
                                <Lightbulb size={10} /> Metodologia
                              </label>
                              <input
                                type="text"
                                value={editDados.metodologia || ''}
                                onChange={(e) => setEditDados((p) => ({ ...p, metodologia: e.target.value }))}
                              />
                            </div>
                            <div className="diario-edit-field">
                              <label>
                                <Wrench size={10} /> Recursos
                              </label>
                              <input
                                type="text"
                                value={editDados.recursosUtilizados || ''}
                                onChange={(e) =>
                                  setEditDados((p) => ({ ...p, recursosUtilizados: e.target.value }))
                                }
                              />
                            </div>
                            <div className="diario-edit-field">
                              <label>Observacoes</label>
                              <textarea
                                value={editDados.observacoes || ''}
                                onChange={(e) => setEditDados((p) => ({ ...p, observacoes: e.target.value }))}
                                rows={2}
                              />
                            </div>
                            <div
                              onClick={() =>
                                setEditDados((p) => ({ ...p, objetivosCumpridos: !p.objetivosCumpridos }))
                              }
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                cursor: 'pointer',
                                padding: '2px 0',
                              }}
                            >
                              <div
                                style={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 5,
                                  border: `2px solid ${editDados.objetivosCumpridos ? '#2563eb' : 'var(--border-color, #d1d5db)'}`,
                                  background: editDados.objetivosCumpridos ? '#2563eb' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s',
                                }}
                              >
                                {editDados.objetivosCumpridos && (
                                  <CheckCircle2 size={10} style={{ color: '#fff' }} />
                                )}
                              </div>
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-main)' }}>
                                Objetivos cumpridos
                              </span>
                            </div>
                            <div className="diario-edit-actions">
                              <button
                                onClick={cancelarEdicao}
                                style={{
                                  border: '1px solid var(--border-color, #d1d5db)',
                                  background: 'transparent',
                                  color: 'var(--text-muted)',
                                }}
                              >
                                <X size={10} /> Cancelar
                              </button>
                              <button
                                onClick={() => salvarEdicao()}
                                disabled={salvandoEdit}
                                style={{
                                  border: 'none',
                                  background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                                  color: '#fff',
                                  opacity: salvandoEdit ? 0.6 : 1,
                                }}
                              >
                                <Save size={10} /> {salvandoEdit ? 'Salvando...' : 'Salvar'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MobilePageWrapper>
  );
}
